"use client";

import { useLiveQuery } from "@sanity/preview-kit";
import { defineQuery } from "next-sanity";
import { PricingItem } from "@/sanity/lib/getPriceList";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Star } from "lucide-react";

interface Props {
  initialData: PricingItem[];
}

const PRICE_LIST_QUERY = defineQuery(`
  *[_type == "pricingItem"] | order(order asc) {
    _id,
    serviceName,
    price
  }
`);

export default function PricingSection({ initialData }: Props) {
  const [data] = useLiveQuery<PricingItem[]>(initialData, PRICE_LIST_QUERY);

  if (!data || data.length === 0) return null;

  return (
    <section id="cenik" className="relative py-20 px-4 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-5xl mx-auto">

        {/* Nadpis */}
        <div className="text-center mb-12">
          <h2 className="font-bold text-4xl md:text-5xl mb-4 bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
            Ceník služeb
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Transparentní ceny za profesionální broušení a údržbu nástrojů
          </p>
        </div>

        {/* DESKTOP TABULKA */}
        <Card className="shadow-xl border-gray-200/50 overflow-hidden hidden sm:block">
          <CardHeader className="bg-gradient-to-r from-gray-50 to-white border-b">
            <CardTitle className="text-xl flex items-center gap-2" style={{ letterSpacing: "0.03em" }}>
              <Star className="h-5 w-5 text-primary" />
              Přehled služeb a cen
            </CardTitle>
          </CardHeader>

          <CardContent className="p-0">
            <div className="overflow-x-auto scrollbar-hide">
              <Table className="min-w-full">
                <TableHeader>
                  <TableRow className="bg-gray-50/50 hover:bg-gray-50/50">
                    <TableHead>Název služby</TableHead>
                    <TableHead className="text-right w-[200px]">Cena</TableHead>
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {data.map((item, index) => (
                    <TableRow key={item._id} className="group hover:bg-primary/5 transition-colors">
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-3">
                          <span className="group-hover:text-primary transition-colors">
                            {item.serviceName}
                          </span>
                          {index === 0 && (
                            <Badge variant="default" className="text-xs">Populární</Badge>
                          )}
                        </div>
                      </TableCell>

                      <TableCell className="text-right whitespace-nowrap">
                        <span className="text-lg font-bold text-primary group-hover:scale-105 transition-transform inline-block">
                          {item.price}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* MOBILNÍ STACKED CARDS */}
        <div className="sm:hidden space-y-4 mt-6">
          {data.map((item, index) => (
            <Card key={item._id} className="p-4 shadow-md border border-gray-200">
              <div className="flex justify-between items-center mb-2">
                <span className="font-medium text-base">{item.serviceName}</span>
                {index === 0 && (
                  <Badge variant="default" className="text-xs">Populární</Badge>
                )}
              </div>

              <p className="text-primary font-bold text-xl">{item.price}</p>
            </Card>
          ))}
        </div>

        {/* Poznámka */}
        <div className="mt-8 text-center">
          <p className="text-gray-500 text-sm">
            Všechny ceny jsou orientační. Konečná cena závisí na stavu nástroje.
          </p>
        </div>
      </div>
    </section>
  );
}
